import { Component, Prop, h, Host, Build } from '@stencil/core';

const COMPONENT_NAME = 'sn-call-to-action-type-5';

/**
 * Blue CTA banner for SignNow forms pages.
 * Centered layout with label, heading, and a "Get Form" button that opens the form editor.
 * Dispatches the `sn-form-loader-open` custom event when the action button is clicked.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Call-to-action banner for encouraging users to create forms
 * @example
 *   <sn-call-to-action-type-5 form-id="my-form-id">
 *     <me-text slot="label" tag="p" class="me-text--label">BE READY TO GET MORE</me-text>
 *     <me-heading slot="heading" variant="2" tag="h2">Create this form in 5 minutes or less</me-heading>
 *     <me-button slot="action-primary" variant="primary" tag="button" data-color-mode="inverted">Get Form</me-button>
 *   </sn-call-to-action-type-5>
 * @slot label - <me-text tag="p" class="me-text--label"> - Uppercase label above the heading
 * @slot heading - {required}, <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main CTA heading
 * @slot action-primary - {required}, <me-button variant="primary"> - Action button (triggers form loader)
 */
@Component({
  tag: 'sn-call-to-action-type-5',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCallToActionType5 {
  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Unique form identifier passed as detail to the `sn-form-loader-open` event
   */
  @Prop({ attribute: 'form-id', reflect: true }) formId?: string;

  private handleCtaClick = (event: Event) => {
    event.preventDefault();
    if (!Build.isBrowser) return;
    const formId = this.formId || (document.querySelector('[data-formid]') as HTMLElement)?.dataset?.formid;
    window.dispatchEvent(new CustomEvent('sn-form-loader-open', { detail: formId }));
  };

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME}>
          <div class={`${COMPONENT_NAME}__container`}>
            <div class={`${COMPONENT_NAME}__inner`}>
              <slot name="label"></slot>
              <slot name="heading"></slot>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div class={`${COMPONENT_NAME}__action js-editor-link`} onClick={this.handleCtaClick}>
                <slot name="action-primary"></slot>
              </div>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
